import React, { useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { CloudDownload, Loader2, X } from 'lucide-react';

const DEFAULT_PROD_URL = 'https://pnl-dashboard-8.emergent.host';

const SnapshotRefresh = () => {
  const [open, setOpen] = useState(false);
  const [prodUrl, setProdUrl] = useState(DEFAULT_PROD_URL);
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setOpen(false);
    setPassword('');
    setResult(null);
    setBusy(false);
  };

  const run = async () => {
    if (!prodUrl || !username || !password) {
      toast.error('Production URL, username and password are required');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await axiosInstance.post('/admin/snapshot/refresh-from-prod', {
        prod_url: prodUrl,
        username,
        password,
      });
      setResult(res.data);
      toast.success('Production data pulled into preview successfully');
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Refresh failed';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setBusy(false);
    }
  };

  const totalAfter = result?.after
    ? Object.values(result.after).reduce((a, b) => a + b, 0)
    : null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="cb-btn-secondary cb-btn-secondary--negative"
        data-testid="snapshot-refresh-open"
      >
        <CloudDownload className="w-4 h-4" />
        Refresh from Production
      </button>

      {open && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: 'rgba(11,11,10,0.55)' }}
          data-testid="snapshot-refresh-modal"
        >
          <div className="cb-card w-full max-w-lg" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="cb-section-title">Refresh from Production</h2>
                <p className="cb-section-sub" style={{ marginTop: 'var(--cb-space-1)' }}>
                  Pulls the latest data from production into this preview database.
                  User accounts are NOT touched, so your preview logins keep working.
                </p>
              </div>
              <button
                onClick={reset}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}
                data-testid="snapshot-refresh-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="cb-card-label" style={{ display: 'block', marginBottom: 'var(--cb-space-1)' }}>
                  Production URL
                </label>
                <input
                  type="url"
                  value={prodUrl}
                  onChange={(e) => setProdUrl(e.target.value)}
                  className="form-input"
                  style={{ fontFamily: 'monospace' }}
                  data-testid="snapshot-prod-url"
                />
              </div>
              <div>
                <label className="cb-card-label" style={{ display: 'block', marginBottom: 'var(--cb-space-1)' }}>
                  Production admin username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="form-input"
                  data-testid="snapshot-prod-username"
                />
              </div>
              <div>
                <label className="cb-card-label" style={{ display: 'block', marginBottom: 'var(--cb-space-1)' }}>
                  Production admin password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  data-testid="snapshot-prod-password"
                />
              </div>
            </div>

            <div className="mt-4" style={{ padding: 'var(--cb-space-3)', borderRadius: 'var(--cb-radius)', background: 'var(--cb-bone)', border: '1px solid var(--cb-line)', fontSize: '0.75rem', color: 'var(--cb-ink)' }}>
              <strong style={{ color: 'var(--cb-mark)' }}>Heads up:</strong> All current preview data (clients, ledger, cycles, etc.)
              will be wiped and replaced with the production snapshot. This action is irreversible
              for the preview database.
            </div>

            {result && (
              <div className="mt-4" style={{ padding: 'var(--cb-space-3)', borderRadius: 'var(--cb-radius)', background: 'var(--cb-bone)', border: '1px solid var(--cb-positive)', fontSize: '0.75rem', color: 'var(--cb-ink)' }} data-testid="snapshot-refresh-result">
                <p style={{ fontWeight: 600, color: 'var(--cb-positive)' }}>Imported {totalAfter} documents.</p>
                <ul className="mt-1 grid grid-cols-2 gap-x-4" style={{ listStyle: 'none', padding: 0 }}>
                  {Object.entries(result.after).map(([k, v]) => (
                    <li key={k} className="flex justify-between cb-num" style={{ fontFamily: 'monospace' }}>
                      <span>{k}</span>
                      <span>{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={reset}
                disabled={busy}
                className="cb-btn-secondary"
                data-testid="snapshot-refresh-cancel"
              >
                {result ? 'Close' : 'Cancel'}
              </button>
              {!result && (
                <button
                  onClick={run}
                  disabled={busy || !password}
                  className="cb-btn-primary"
                  data-testid="snapshot-refresh-confirm"
                >
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  {busy ? 'Pulling…' : 'Pull & Replace'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SnapshotRefresh;
